import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getAllTillDevices,
  getTillToTillShareOrders,
  shareMultipleOrdersToTills,
  shareOrderToTill,
} from "@/api/new-api/till-to-till-share/till-to-till-share";

export const fetchTillDeviceList = createAsyncThunk(
  "tillToTillShare/fetchTillDeviceList",
  async (_, { rejectWithValue }) => {
    const res = await getAllTillDevices();
    if (res?.status === "success") {
      return res?.data?.tills || [];
    }

    return rejectWithValue(res?.message || "Failed to fetch till devices");
  },
);

export const fetchTillToTillShareOrders = createAsyncThunk(
  "tillToTillShare/fetchOrders",
  async ({ restaurantId, params }, { rejectWithValue }) => {
    const res = await getTillToTillShareOrders(restaurantId, params);

    if (res?.status === "success") {
      return {
        orders: res?.data?.orders || [],
        pagination: res?.data?.pagination || null,
      };
    }

    return rejectWithValue(res?.message || "Failed to fetch till share orders");
  },
);

export const shareSingleOrderTillThunk = createAsyncThunk(
  "tillToTillShare/shareSingleOrderTill",
  async ({ orderId, tillId }, { rejectWithValue }) => {
    const res = await shareOrderToTill(orderId, { till_id: tillId });

    if (res?.status === "success") {
      return res?.data || {};
    }

    return rejectWithValue(res?.message || "Failed to share order till");
  },
);

export const shareMultipleOrdersTillThunk = createAsyncThunk(
  "tillToTillShare/shareMultipleOrdersTill",
  async ({ restaurantId, orderIds, tillIds }, { rejectWithValue }) => {
    const res = await shareMultipleOrdersToTills(restaurantId, {
      order_ids: orderIds,
      till_ids: tillIds,
    });

    if (res?.status === "success") {
      return res?.data || {};
    }

    return rejectWithValue(res?.message || "Failed to share multiple orders");
  },
);

const initialState = {
  tills: [],
  orders: [],
  pagination: null,
  tillDevicesLoading: false,
  ordersLoading: false,
  singleShareLoading: false,
  multipleShareLoading: false,
  tillDevicesError: null,
  ordersError: null,
  singleShareError: null,
  multipleShareError: null,
};

const tillToTillShareSlice = createSlice({
  name: "tillToTillShare",
  initialState,
  reducers: {
    clearTillToTillShareErrors: (state) => {
      state.tillDevicesError = null;
      state.ordersError = null;
      state.singleShareError = null;
      state.multipleShareError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTillDeviceList.pending, (state) => {
        state.tillDevicesLoading = true;
        state.tillDevicesError = null;
      })
      .addCase(fetchTillDeviceList.fulfilled, (state, action) => {
        state.tillDevicesLoading = false;
        state.tills = action.payload || [];
      })
      .addCase(fetchTillDeviceList.rejected, (state, action) => {
        state.tillDevicesLoading = false;
        state.tillDevicesError = action.payload;
      })
      .addCase(fetchTillToTillShareOrders.pending, (state) => {
        state.ordersLoading = true;
        state.ordersError = null;
      })
      .addCase(fetchTillToTillShareOrders.fulfilled, (state, action) => {
        state.ordersLoading = false;
        state.orders = action.payload?.orders || [];
        state.pagination = action.payload?.pagination || null;
      })
      .addCase(fetchTillToTillShareOrders.rejected, (state, action) => {
        state.ordersLoading = false;
        state.ordersError = action.payload;
      })
      .addCase(shareSingleOrderTillThunk.pending, (state) => {
        state.singleShareLoading = true;
        state.singleShareError = null;
      })
      .addCase(shareSingleOrderTillThunk.fulfilled, (state) => {
        state.singleShareLoading = false;
      })
      .addCase(shareSingleOrderTillThunk.rejected, (state, action) => {
        state.singleShareLoading = false;
        state.singleShareError = action.payload;
      })
      .addCase(shareMultipleOrdersTillThunk.pending, (state) => {
        state.multipleShareLoading = true;
        state.multipleShareError = null;
      })
      .addCase(shareMultipleOrdersTillThunk.fulfilled, (state, action) => {
        state.multipleShareLoading = false;

        const updatedOrders = action.payload?.updated;
        if (!Array.isArray(updatedOrders) || updatedOrders.length === 0) {
          return;
        }

        const updatedOrderMap = new Map(
          updatedOrders
            .filter((item) => item?.order_id)
            .map((item) => [
              Number(item.order_id),
              Array.isArray(item.shared_till_ids) ? item.shared_till_ids : [],
            ]),
        );

        state.orders = (state.orders || []).map((order) => {
          const orderId = Number(order?.id);
          if (!updatedOrderMap.has(orderId)) {
            return order;
          }

          return {
            ...order,
            shared_till_ids: updatedOrderMap.get(orderId),
          };
        });
      })
      .addCase(shareMultipleOrdersTillThunk.rejected, (state, action) => {
        state.multipleShareLoading = false;
        state.multipleShareError = action.payload;
      });
  },
});

export const { clearTillToTillShareErrors } = tillToTillShareSlice.actions;
export default tillToTillShareSlice.reducer;
